// @flow

import React, { useEffect, useState, useRef } from "react";
import { localize } from "redux-i18n";
import { isAfter, isEqual } from "date-fns";
import snakecase from "snakecase-keys";
import config from "@config";
import { toast } from "react-toastify";
import { SchemaForm, View, Button, ConfirmationModal, Text } from "@components";
import type { EmployeeType, EmployeeFormData } from "@modules/employee/types";
import { unpatch } from "@transformers/js-client";
import type { PropertiesSchema } from "@types";
import { emailPattern } from "@utils/schema";
import uiSchema from "./ui-schema";

type Props = $Exact<{
  employee: EmployeeType,
  employeeFormData: EmployeeFormData,
  clientId: number,
  onSave: Object => void,
  schema: PropertiesSchema,
  hasAdminPermissions: boolean,
  currentUserType: string,
  currentUserPermissions: { [permission: string]: boolean } | null,
  onDeleteEmployee: Function,
  ...PropsFromLocalize,
}>;

export function PersonalDetails(props: Props) {
  const formRef = useRef(null);
  const { employeeFormData } = props;
  const employeeId = employeeFormData.id;

  const [showConfirmDeleteEmployeeModal, setShowConfirmDeleteEmployeeModal] = useState(false);
  const canDeleteEmployee = ["CLIENT_ADMIN", "ADMIN", "SERVICE_MANAGER"].includes(
    props.currentUserType,
  );
  const canRunDeleteQuery =
    props.currentUserPermissions && props.currentUserPermissions["privilege.employee.delete"];

  const [state, setState] = useState({
    edit: false,
    formData: employeeFormData,
  });
  const [showDisableEmployeeConfirmModal, setShowDisableEmployeeConfirmModal] = useState(false);

  useEffect(() => {
    setState({
      ...state,
      formData: employeeFormData,
    });
  }, [employeeFormData]);

  const uischema = uiSchema(
    { dateOfBirth: employeeFormData.dateOfBirth, joinDate: employeeFormData.joinDate },
    props.hasAdminPermissions,
    props.t,
    config,
    state.edit,
  );

  const schema = {
    ...props.schema,
    properties: {
      ...props.schema.properties,
      securityNotice: {
        title: props.t("EMPLOYEE_EMAIL_SECURITY_NOTICE"),
        type: "string",
      },
    },
  };

  function handleChange(data: Object) {
    setState({
      ...state,
      formData: data,
    });
  }

  function onSave(formData: Object) {
    const data = unpatch(snakecase(formData));
    props.onSave({
      clientId: props.clientId,
      employeeId: formData.id,
      formData: data,
    });
    toggleEdit();
  }

  function validate(formData: Object, errors: Object) {
    const { dateOfBirth, joinDate, leaveDate } = formData;

    if (isAfter(new Date(dateOfBirth), new Date(joinDate))) {
      errors.joinDate.addError("Date should be after Date Of Birth");
    }

    if (
      isAfter(new Date(joinDate), new Date(leaveDate)) ||
      isEqual(new Date(joinDate), new Date(leaveDate))
    ) {
      errors.leaveDate.addError("Date should be after Employment Start Date");
    }

    if (formData.workEmail && !emailPattern.test(formData.workEmail)) {
      errors.workEmail.addError("Invalid email format");
    }

    if (formData.homeEmail && !emailPattern.test(formData.homeEmail)) {
      errors.homeEmail.addError("Invalid email format");
    }

    if (formData.employmentTypeId === -1) {
      errors.employmentTypeId.addError("This field is required");
    }

    return errors;
  }

  function submitForm() {
    // before submit the schema form check if any changes are made and check if the email is missing
    // if the email is deleted, but exist in the original data, the user should confirm disabling the user account
    if (
      state.formData &&
      employeeFormData.workEmail !== state.formData.workEmail &&
      !state.formData.workEmail
    ) {
      setShowDisableEmployeeConfirmModal(true);
    } else {
      formRef.current.submit();
    }
  }

  function cancelEdit() {
    setState({
      edit: !state.edit,
      formData: employeeFormData,
    });
  }

  function toggleEdit() {
    setState({
      ...state,
      edit: !state.edit,
    });
  }

  if (!(props.employee || {})) {
    return null;
  }

  function toggleConfirmDeleteEmployeeModal() {
    setShowConfirmDeleteEmployeeModal(!showConfirmDeleteEmployeeModal);
  }

  function deleteEmployee() {
    setShowConfirmDeleteEmployeeModal(!showConfirmDeleteEmployeeModal);
    (canRunDeleteQuery &&
      props.onDeleteEmployee({
        clientId: props.clientId,
        employeeId,
      })) ||
      toast.error(props.t("YOU_DON_T_HAVE_PRIVILEGE_EMPLOYEE_DELETE"));
  }

  function disableEmployee() {
    setShowDisableEmployeeConfirmModal(false);
    formRef.current.submit();
  }

  function toggleDisableConfirmEmployeeModal() {
    setShowDisableEmployeeConfirmModal(!showDisableEmployeeConfirmModal);
  }

  return (
    <>
      <View className="gs-container" gap="6">
        <View direction="row" justifyContent="space-between">
          <Text type="h6">{props.t("EMPLOYEE_DETAILS")}</Text>
          {props.hasAdminPermissions && (
            <View direction="row" gap="4">
              {!state.edit ? (
                <Button materialIcon="edit" onClick={toggleEdit} size="small">
                  {props.t("EDIT")}
                </Button>
              ) : (
                <View direction="row" gap="4">
                  <Button color="secondary" onClick={cancelEdit} size="small">
                    {props.t("CANCEL")}
                  </Button>
                  <Button
                    materialIcon="replay_circle_filled"
                    className="ml-2"
                    onClick={submitForm}
                    size="small"
                  >
                    {props.t("SUBMIT")}
                  </Button>
                </View>
              )}
              {canDeleteEmployee && (
                <Button
                  color="negative"
                  onClick={toggleConfirmDeleteEmployeeModal}
                  label={props.t("DELETE_EMPLOYEE_RECORD")}
                  size="small"
                />
              )}
            </View>
          )}
        </View>
        <SchemaForm
          ref={formRef}
          schema={schema}
          formData={state.formData}
          onSubmit={onSave}
          uiSchema={uischema}
          readonly={!state.edit}
          onChange={handleChange}
          externalSubmit
          columns={3}
        />
      </View>

      {showConfirmDeleteEmployeeModal && (
        <ConfirmationModal
          headerText={props.t("ARE_YOU_SURE")}
          bodyContent={props.t("YOU_SURE_WANT_DELETE_EMPLOYEE_DETAILED_DESCRIPTION")}
          cancelBtnLabel={props.t("NO_KEEP_EMPLOYEE")}
          cancelBtnColor="primary"
          confirmBtnLabel={props.t("YES_DELETE_EMPLOYEE")}
          confirmBtnIcon="replay_circle_filled"
          confirmBtnColor="negative"
          modalSize="normal"
          onCancel={toggleConfirmDeleteEmployeeModal}
          onConfirm={deleteEmployee}
        />
      )}

      {showDisableEmployeeConfirmModal && (
        <ConfirmationModal
          headerText={props.t("ARE_YOU_SURE")}
          bodyContent={props.t("DISABLE_USER_ACCOUNT_CONFIRMATION_MESSAGE")}
          cancelBtnLabel={props.t("CANCEL")}
          cancelBtnColor="primary"
          confirmBtnLabel={props.t("YES_DISABLE_EMAIL")}
          confirmBtnIcon="replay_circle_filled"
          confirmBtnColor="negative"
          modalSize="normal"
          onCancel={toggleDisableConfirmEmployeeModal}
          onConfirm={disableEmployee}
        />
      )}
    </>
  );
}

export default localize()(PersonalDetails);
